import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { ExternalLink, Pencil } from 'lucide-react';

import { Flex, FlexColumn } from 'src/components/flex';
import FactRow from 'src/components/lead-sidebar/FactRow';
import LeadSidebarExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import LeadSidebarPropertyNotes from 'src/components/lead-sidebar/LeadSidebarPropertyNotes';
import CreateOrUpdatePropertyModal from 'src/components/create-or-update-property-modal/CreateOrUpdatePropertyModal';
import {
    resolvePropertyContactAssignments,
    useContacts,
    useProperties,
    useProperty,
} from 'src/services/api';
import { contactDisplayName } from 'src/services/api/contacts/contact-display-name';
import type { IProperty } from 'src/services/api/properties/types';
import { getDisplayedPropertyFacts } from 'src/components/lead-sidebar/displayed-property-facts';

import styles from 'src/components/lead-sidebar/LeadSidebarPropertyTab.module.css';

function formatAddress(p: IProperty): string {
    const street = p.unit ? `${p.street_address}, Unit ${p.unit}` : p.street_address;
    return `${street}, ${p.city}, ${p.state} ${p.zipcode}`;
}

function NoKnownFacts() {
    return (
        <Typography variant="body2" color="text.secondary" className={styles.noKnownFacts}>
            no known facts
        </Typography>
    );
}

interface UnitSectionProps {
    property: IProperty;
    title: string;
}

function UnitSection({ property, title }: UnitSectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = getDisplayedPropertyFacts(property.facts);
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-unit-property-button">
            <Pencil size={16} />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title={title} defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {property.name}
                    </Typography>
                    <FactRow label="Address" value={formatAddress(property)} factKey="address" />
                    {property.rent != null && (
                        <FactRow label="Rent" value={`$${property.rent.toLocaleString()}/mo`} factKey="rent" />
                    )}
                    {property.showing_scheduling_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Schedule a tour</Typography>
                            <Link href={property.showing_scheduling_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <ExternalLink size={13} className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {property.application_link && (
                        <div className={styles.factRow}>
                            <Typography variant="body2" color="text.secondary">Apply</Typography>
                            <Link href={property.application_link.link} target="_blank" rel="noreferrer" variant="body2">
                                <span className={styles.linkRow}>
                                    Open link <ExternalLink size={13} className={styles.externalLinkIcon} />
                                </span>
                            </Link>
                        </div>
                    )}
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={fact.label}
                            value={fact.value}
                            factKey={fact.key}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={property}
                    initialSection="propertyFacts"
                />
            )}
        </>
    );
}

interface CommunitySectionProps {
    community: IProperty;
}

function CommunitySection({ community }: CommunitySectionProps) {
    const [isEditing, setIsEditing] = useState(false);
    const facts = getDisplayedPropertyFacts(community.facts);
    const editAction = (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }} data-testid="edit-community-property-button">
            <Pencil size={16} />
        </IconButton>
    );
    return (
        <>
            <LeadSidebarExpandableItem title={`${community.type ?? 'Community'} Facts`} defaultExpanded action={editAction}>
                <FlexColumn rowGap={1.5} pb={1}>
                    <Typography variant="body2" fontWeight={500} className={styles.sectionName}>
                        {community.name}
                    </Typography>
                    {facts.map((fact) => (
                        <FactRow
                            key={fact.key}
                            label={fact.label}
                            value={fact.value}
                            factKey={fact.key}
                        />
                    ))}
                    {facts.length === 0 && <NoKnownFacts />}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditing && (
                <CreateOrUpdatePropertyModal
                    opened={isEditing}
                    onClose={() => setIsEditing(false)}
                    property={community}
                    initialSection="propertyFacts"
                />
            )}
        </>
    );
}

type Props = {
    leadPropertyId: number | null;
};

export default function LeadSidebarPropertyTab({ leadPropertyId }: Props) {
    const { data: property, isLoading } = useProperty(
        leadPropertyId,
        {
            include_ancestors: true,
            include_facts: true,
            include_integrations: true,
        },
        // this data feeds CreateOrUpdatePropertyModal below — a background
        // refetch on window focus would silently overwrite unsaved edits
        false,
    );
    const { data: contacts = [] } = useContacts();
    const { data: properties = [] } = useProperties();
    const [isEditingContacts, setIsEditingContacts] = useState(false);

    if (!leadPropertyId) {
        return (
            <Typography variant="body2" color="text.secondary" p={2}>
                This lead is not associated with a property.
            </Typography>
        );
    }

    if (isLoading) {
        return (
            <Flex justifyContent="center" py={2}>
                <CircularProgress size={20} />
            </Flex>
        );
    }

    if (!property) { return null; }

    const unitTitle = property.parent ? 'Unit Facts' : 'Property Facts';
    const contactsProperty = property.parent ?? property;
    const parentTypeLabel = contactsProperty.type ?? 'Property';
    const contactAssignments = resolvePropertyContactAssignments(contacts, properties, contactsProperty.id);
    const propertyContacts = contactAssignments.map(({ contact }) => contact);
    const areContactsEnabled = contactAssignments[0]?.enabled ?? contactsProperty.is_contacts_invited;
    const contactsActions = (
        <div className={styles.contactsActions}>
            <IconButton
                size="small"
                onClick={(event) => {
                    event.stopPropagation();
                    setIsEditingContacts(true);
                }}
                aria-label="Edit property contacts"
                data-testid="edit-property-contacts-button"
            >
                <Pencil size={16} />
            </IconButton>
        </div>
    );

    return (
        <>
            <LeadSidebarPropertyNotes property={property} />
            <UnitSection property={property} title={unitTitle} />
            <LeadSidebarExpandableItem
                title={`${parentTypeLabel} Contacts`}
                defaultExpanded={propertyContacts.length !== 0}
                action={contactsActions}
            >
                <FlexColumn rowGap={1} pb={1}>
                    <Typography variant="body2" color="text.secondary" aria-label="Calendar invite sync status">
                        {areContactsEnabled ? 'Calendar Invites will be send to Contacts' : 'Calendar Invites will not be send to Contacts'}
                    </Typography>
                    {propertyContacts.map((contact) => (
                        <FactRow
                            key={contact.id}
                            label={contactDisplayName(contact)}
                            value={contact.email}
                            factKey={`contact-${contact.id}`}
                        />
                    ))}
                    {propertyContacts.length === 0 && (
                        <Typography variant="body2" color="text.secondary" className={styles.noKnownFacts}>
                            no contacts associated with this property
                        </Typography>
                    )}
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {isEditingContacts && (
                <CreateOrUpdatePropertyModal
                    opened={isEditingContacts}
                    onClose={() => setIsEditingContacts(false)}
                    property={contactsProperty}
                    initialSection="contacts"
                />
            )}
            {property.parent && <CommunitySection community={property.parent} />}
        </>
    );
}
